import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IoTrashOutline } from 'react-icons/io5';

import { Table } from 'src/components/TableNew';
import { Button } from 'src/components/Button';
import { SearchInput } from 'src/components/SearchInput';
import Badge from 'src/components/Table/Row/Badge';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import {
  UserDeposit,
  UserDepositSummary,
  fetchPlatformUserDeposits,
  fetchPlatformUserDepositsSummary,
} from 'src/api/adminUsers';
import { convertDate, shortenString } from 'src/components/Table/helpers';
import { useTheme } from 'src/useTheme';

import {
  ActionIconButton,
  ChartCanvas,
  ChartHeader,
  ChartLegend,
  ChartLegendDot,
  ChartStat,
  ChartStatSub,
  ChartSvg,
  ChartWrap,
  CompactSelect,
  EmptyState,
  SearchControl,
  SectionControls,
  SectionTitle,
  SectionToolbar,
  TabPanel,
} from '../styled';

type DepositsTabProps = {
  userId: string;
  onForbidden?: () => void;
};

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatMonthLabel = (month: string) => {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return month;
  const idx = Number(match[2]) - 1;
  return MONTH_SHORT[idx] ?? month;
};

const formatAxisValue = (value: number) => {
  if (value >= 1_000_000) return `${Math.round(value / 1_000_000)}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}k`;
  return String(Math.round(value));
};

const niceCeiling = (value: number) => {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
};

const buildSmoothPath = (points: Array<{ x: number; y: number }>) => {
  if (!points.length) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const current = points[i];
    const next = points[i + 1];
    const cx = (current.x + next.x) / 2;
    d += ` C ${cx} ${current.y}, ${cx} ${next.y}, ${next.x} ${next.y}`;
  }
  return d;
};

const DepositsTab: React.FC<DepositsTabProps> = ({ userId, onForbidden }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [deposits, setDeposits] = useState<UserDeposit[]>([]);
  const [summary, setSummary] = useState<UserDepositSummary | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [months, setMonths] = useState('12');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [search, setSearch] = useState('');
  const [depositToFilter, setDepositToFilter] = useState('all');

  const notifyComingSoon = (feature: string) => {
    dispatch(
      setAppAlert({
        message: `${feature} is not available yet — API pending.`,
        isSuccess: false,
      }),
    );
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const listRes = await fetchPlatformUserDeposits(userId, {
        page: currentPage,
        limit: 10,
        currency_code: currencyFilter || undefined,
      });
      setDeposits(listRes.deposits);
      setTotalPages(Math.max(1, listRes.meta.page_count || 1));
      setTotalCount(listRes.meta.total_count || listRes.deposits.length);

      try {
        const summaryRes = await fetchPlatformUserDepositsSummary(userId, {
          months: Number(months) || 12,
          currency: 'USD',
          currency_code: currencyFilter || undefined,
        });
        setSummary(summaryRes);
      } catch {
        // Chart optional
      }
    } catch (e: any) {
      if (e?.response?.status === 403) {
        onForbidden?.();
        return;
      }
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? 'Failed to load deposits',
          isSuccess: false,
        }),
      );
      setDeposits([]);
    } finally {
      setLoading(false);
    }
  }, [userId, currentPage, months, currencyFilter, dispatch, onForbidden]);

  useEffect(() => {
    load();
  }, [load]);

  const chartModel = useMemo(() => {
    const series = summary?.series ?? [];
    const values = series.map((point) => Number(point.value) || 0);
    const maxValue = niceCeiling(Math.max(...values, 1));
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => maxValue * ratio);

    const width = 960;
    const height = 240;
    const pad = { top: 12, right: 12, bottom: 28, left: 36 };
    const plotW = width - pad.left - pad.right;
    const plotH = height - pad.top - pad.bottom;
    const count = Math.max(series.length, 1);

    const points = series.map((point, index) => {
      const x =
        pad.left + (count === 1 ? plotW / 2 : (index / Math.max(count - 1, 1)) * plotW);
      const y = pad.top + plotH - ((Number(point.value) || 0) / maxValue) * plotH;
      return {
        x,
        y,
        label: formatMonthLabel(point.month),
        value: Number(point.value) || 0,
        count: point.count,
        month: point.month,
      };
    });

    return {
      width,
      height,
      pad,
      plotW,
      plotH,
      maxValue,
      ticks,
      points,
      linePath: buildSmoothPath(points),
    };
  }, [summary]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return deposits.filter((deposit) => {
      if (depositToFilter !== 'all') {
        const target = String(deposit.deposit_to || deposit.id || '').toLowerCase();
        if (!target.includes(depositToFilter.toLowerCase())) return false;
      }
      if (!q) return true;
      return [
        deposit.id,
        deposit.deposit_to,
        deposit.amount,
        deposit.currency_code,
        deposit.description,
        deposit.status,
        deposit.kind,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [deposits, search, depositToFilter]);

  const rows = filtered.map((deposit) => ({
    ...deposit,
    id: deposit.id,
    deposit_to_label: deposit.deposit_to || shortenString(deposit.id) || '—',
    amount_label: deposit.amount || '—',
    deposit_ccy: deposit.currency_code || '—',
    type_label: deposit.description || (deposit.kind === 'MANUAL_DEPOSIT' ? 'Manual' : 'On-chain'),
    status: deposit.status,
    created_at: deposit.created_at,
  }));

  const totalDisplay = summary?.total
    ? Number(summary.total).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    : '0';

  return (
    <TabPanel>
      <ChartWrap>
        <ChartHeader>
          <SectionTitle>Deposits</SectionTitle>
          <SectionControls>
            <CompactSelect
              value={currencyFilter}
              onChange={(e) => {
                setCurrencyFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">Deposit Currency (All)</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="BTC">BTC</option>
              <option value="ETH">ETH</option>
            </CompactSelect>
            <CompactSelect
              value={months}
              onChange={(e) => {
                setMonths(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="3">Last 3 Months</option>
              <option value="6">Last 6 Months</option>
              <option value="12">Last 12 Months</option>
            </CompactSelect>
          </SectionControls>
        </ChartHeader>

        {summary ? (
          <>
            <ChartStat>${totalDisplay}</ChartStat>
            <ChartStatSub>
              {summary.count.toLocaleString()} Deposits
              {(summary.unpriced?.length ?? 0) > 0
                ? ` · ${summary.unpriced?.length} unpriced`
                : ''}
            </ChartStatSub>
            <ChartCanvas>
              <ChartSvg
                viewBox={`0 0 ${chartModel.width} ${chartModel.height}`}
                preserveAspectRatio="none"
                role="img"
              >
                {chartModel.ticks.map((tick) => {
                  const y =
                    chartModel.pad.top +
                    chartModel.plotH -
                    (tick / chartModel.maxValue) * chartModel.plotH;
                  return (
                    <g key={tick}>
                      <line
                        x1={chartModel.pad.left}
                        x2={chartModel.pad.left + chartModel.plotW}
                        y1={y}
                        y2={y}
                        stroke={theme.grey}
                        strokeWidth={1}
                      />
                      <text
                        x={chartModel.pad.left - 8}
                        y={y + 4}
                        textAnchor="end"
                        fill={theme.midGrey}
                        fontSize={11}
                      >
                        {formatAxisValue(tick)}
                      </text>
                    </g>
                  );
                })}
                {chartModel.points.map((point) => (
                  <line
                    key={`v-${point.month}`}
                    x1={point.x}
                    x2={point.x}
                    y1={chartModel.pad.top}
                    y2={chartModel.pad.top + chartModel.plotH}
                    stroke={theme.grey}
                    strokeWidth={1}
                    opacity={0.55}
                  />
                ))}
                {chartModel.linePath && (
                  <path
                    d={chartModel.linePath}
                    fill="none"
                    stroke={theme.primary}
                    strokeWidth={1.25}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}
                {chartModel.points.map((point) => (
                  <g key={point.month}>
                    <circle
                      cx={point.x}
                      cy={point.y}
                      r={3.5}
                      fill={theme.primary}
                      stroke={theme.primary}
                      strokeWidth={1}
                    >
                      <title>
                        {point.label}: ${point.value.toLocaleString()} ({point.count})
                      </title>
                    </circle>
                    <text
                      x={point.x}
                      y={chartModel.height - 6}
                      textAnchor="middle"
                      fill={theme.midGrey}
                      fontSize={11}
                    >
                      {point.label}
                    </text>
                  </g>
                ))}
              </ChartSvg>
            </ChartCanvas>
            <ChartLegend>
              <ChartLegendDot />
              All
            </ChartLegend>
          </>
        ) : (
          <ChartStatSub>Deposit summary unavailable.</ChartStatSub>
        )}
      </ChartWrap>

      <SectionToolbar>
        <SectionTitle>Deposits ({totalCount})</SectionTitle>
        <SectionControls>
          <SearchControl>
            <SearchInput
              inline
              showIcon
              placeholder="Search..."
              value={search}
              onChange={setSearch}
            />
          </SearchControl>
          <CompactSelect value={depositToFilter} onChange={(e) => setDepositToFilter(e.target.value)}>
            <option value="all">Deposit to</option>
            <option value="wallet">Wallet</option>
            <option value="bank">Bank</option>
          </CompactSelect>
          <Button text="Add" className="black" onClick={() => notifyComingSoon('Add deposit')} />
        </SectionControls>
      </SectionToolbar>

      {rows.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={[
            'Deposit to',
            'Amount',
            'Deposit CCY',
            'Type',
            'Status',
            'Created On',
          ]}
          items={rows}
          columns="1.2fr 1fr 0.9fr 1fr 0.9fr 1fr"
          fieldsToShow={[
            'deposit_to_label',
            'amount_label',
            'deposit_ccy',
            'type_label',
            'status',
            'created_at',
          ]}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          disableRowDetails
          actionsHeader="Quick Actions"
          renderCell={(item, field) => {
            if (field === 'status') return <Badge value={item.status ?? '-'} />;
            if (field === 'created_at') {
              return <span>{item.created_at ? convertDate(item.created_at).date : '—'}</span>;
            }
            return null;
          }}
          actions={() => (
            <ActionIconButton
              type="button"
              aria-label="Delete deposit"
              title="Delete deposit"
              onClick={() => notifyComingSoon('Delete deposit')}
            >
              <IoTrashOutline size={16} />
            </ActionIconButton>
          )}
        />
      ) : (
        <EmptyState>No deposits for this user.</EmptyState>
      )}
    </TabPanel>
  );
};

export default DepositsTab;
